import React from 'react';
import { motion } from 'motion/react';
import { ShieldCheck } from 'lucide-react';
import type { User } from '../../types/index.js';

export type NavTab = 'landing' | 'books' | 'dashboard' | 'history' | 'api-docs' | 'profile' | 'blog' | 'admin-blog' | 'showcase-directory' | 'showcase';

interface NavLinksProps {
  currentTab: NavTab;
  user: User | null;
  onSelectTab: (tab: NavTab) => void;
}

export const NavLinks: React.FC<NavLinksProps> = ({ currentTab, user, onSelectTab }) => {
  return (
    <nav className="hidden md:flex items-center gap-1 2xl:gap-1.5 text-xs 2xl:text-sm font-semibold">
      {/* Landing */}
      <button
        id="nav-tab-landing"
        type="button"
        onClick={() => onSelectTab('landing')}
        className={`relative px-3 py-2 rounded-xl transition cursor-pointer ${
          currentTab === 'landing'
            ? 'text-indigo-600 font-bold'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
        }`}
      >
        {currentTab === 'landing' && (
          <motion.span
            layoutId="activeTabBadge"
            className="absolute inset-0 bg-indigo-50/90 rounded-xl -z-10 border border-indigo-200/60 shadow-2xs"
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          />
        )}
        <span>Halaman Utama</span>
      </button>

      {/* Public Directory */}
      <button
        id="nav-tab-directory"
        type="button"
        onClick={() => {
          onSelectTab('showcase-directory');
          window.history.pushState({}, '', '/direktori');
        }}
        className={`relative px-3 py-2 rounded-xl transition cursor-pointer ${
          currentTab === 'showcase-directory' || currentTab === 'showcase'
            ? 'text-indigo-600 font-bold'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
        }`}
      >
        {(currentTab === 'showcase-directory' || currentTab === 'showcase') && (
          <motion.span
            layoutId="activeTabBadge"
            className="absolute inset-0 bg-indigo-50/90 rounded-xl -z-10 border border-indigo-200/60 shadow-2xs"
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          />
        )}
        <span>Direktori Publik</span>
      </button>

      {/* Blog */}
      <button
        id="nav-tab-blog"
        type="button"
        onClick={() => {
          onSelectTab('blog');
          window.history.pushState({}, '', '/blog');
        }}
        className={`relative px-3 py-2 rounded-xl transition cursor-pointer ${
          currentTab === 'blog'
            ? 'text-indigo-600 font-bold'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
        }`}
      >
        {currentTab === 'blog' && (
          <motion.span
            layoutId="activeTabBadge"
            className="absolute inset-0 bg-indigo-50/90 rounded-xl -z-10 border border-indigo-200/60 shadow-2xs"
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          />
        )}
        <span>Blog Literasi</span>
      </button>

      {user && (
        <>
          {/* Books */}
          <button
            id="nav-tab-books"
            type="button"
            onClick={() => onSelectTab('books')}
            className={`relative px-3 py-2 rounded-xl transition cursor-pointer ${
              currentTab === 'books'
                ? 'text-indigo-600 font-bold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
            }`}
          >
            {currentTab === 'books' && (
              <motion.span
                layoutId="activeTabBadge"
                className="absolute inset-0 bg-indigo-50/90 rounded-xl -z-10 border border-indigo-200/60 shadow-2xs"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            )}
            <span>Katalog Buku</span>
          </button>

          {/* Dashboard */}
          <button
            id="nav-tab-dashboard"
            type="button"
            onClick={() => onSelectTab('dashboard')}
            className={`relative px-3 py-2 rounded-xl transition cursor-pointer ${
              currentTab === 'dashboard'
                ? 'text-indigo-600 font-bold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
            }`}
          >
            {currentTab === 'dashboard' && (
              <motion.span
                layoutId="activeTabBadge"
                className="absolute inset-0 bg-indigo-50/90 rounded-xl -z-10 border border-indigo-200/60 shadow-2xs"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            )}
            <span>Statistik Real-Time</span>
          </button>

          {/* History */}
          <button
            id="nav-tab-history"
            type="button"
            onClick={() => onSelectTab('history')}
            className={`relative px-3 py-2 rounded-xl transition cursor-pointer ${
              currentTab === 'history'
                ? 'text-indigo-600 font-bold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
            }`}
          >
            {currentTab === 'history' && (
              <motion.span
                layoutId="activeTabBadge"
                className="absolute inset-0 bg-indigo-50/90 rounded-xl -z-10 border border-indigo-200/60 shadow-2xs"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            )}
            <span>Histori Pemindaian</span>
          </button>

          {/* Admin Panel */}
          {user.role === 'admin' && (
            <button
              id="nav-tab-api-docs"
              type="button"
              onClick={() => onSelectTab('api-docs')}
              className={`relative px-3 py-2 rounded-xl transition flex items-center gap-1.5 cursor-pointer ${
                currentTab === 'api-docs'
                  ? 'text-indigo-700 font-bold'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
              }`}
            >
              {currentTab === 'api-docs' && (
                <motion.span
                  layoutId="activeTabBadge"
                  className="absolute inset-0 bg-indigo-50 rounded-xl -z-10 border border-indigo-200/60 shadow-2xs"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
              <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" />
              <span>Panel Admin</span>
            </button>
          )}
        </>
      )}
    </nav>
  );
};
